import type { LucideIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';

import { EASE_OUT_QUINT } from './motion.js';
import { TechText } from './TechText.js';
import { Moldura } from './ui/Moldura.js';

/** O que o sistema faz, em uma linha. Sem "públicas": nem toda obra é. */
const SLOGAN = 'Acompanhamento e fiscalização de obras';

/** A partir daqui a marca vai no tamanho grande. */
const TELA_LARGA = '(min-width: 640px)';

function useTelaLarga(): boolean {
  return useSyncExternalStore(
    (avisar) => {
      const midia = window.matchMedia(TELA_LARGA);
      midia.addEventListener('change', avisar);
      return () => midia.removeEventListener('change', avisar);
    },
    () => window.matchMedia(TELA_LARGA).matches,
  );
}

/**
 * Casca das telas de credencial: login, escolha do ambiente, recuperação, nova
 * senha e a troca obrigatória do primeiro acesso (design.md §8, Tela de
 * credencial).
 *
 * Uma coluna só, centrada, sobre a **prancha** — o quadriculado fino de papel
 * de desenho, que some para as bordas. No alto, **a marca viva** do trilho
 * (`TechText`), grande: a mesma palavra que a pessoa vai encontrar na barra
 * lateral depois de entrar, com a lente varrendo as letras. Embaixo dela, o
 * slogan, e então o **formulário num cartão quadrado dentro da moldura** de
 * prancha técnica — a do banner da obra —, que se desenha ao abrir.
 *
 * Trocar de passo na mesma tela (as credenciais e depois o ambiente; o pedido e
 * depois a confirmação) não remonta a casca: a marca segue varrendo, a moldura
 * fica, e só o conteúdo do cartão troca, subindo.
 */
export function AuthLayout({
  title,
  description,
  icon: Icone,
  children,
  footer,
}: {
  title: string;
  /**
   * Instrução da tela. Ausente no login, onde a marca e o slogan logo acima já
   * dizem o que é o sistema — repetir seria dizer duas vezes a mesma coisa.
   */
  description?: string | undefined;
  /** O ícone do desfecho — enviado, definida, link inválido — sobre o título. */
  icon?: LucideIcon | undefined;
  children: ReactNode;
  footer?: ReactNode | undefined;
}) {
  const larga = useTelaLarga();

  /*
   * Na primeira pintura o conteúdo espera a moldura começar; depois da
   * primeira troca de passo, sobe na hora.
   */
  const [tituloAnterior, setTituloAnterior] = useState(title);
  const [trocou, setTrocou] = useState(false);
  if (title !== tituloAnterior) {
    setTituloAnterior(title);
    setTrocou(true);
  }
  const atraso = trocou ? 0 : 0.35;

  /*
   * Na troca de passo, o botão que levou até ela some com o passo antigo, e o
   * foco cairia no vazio: vai para o título novo, que o leitor de tela anuncia.
   * Se o passo novo já pôs o foco num campo (`autoFocus`), fica onde está.
   */
  const tituloRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!trocou) return;
    const ativo = document.activeElement;
    if (ativo === null || ativo === document.body)
      tituloRef.current?.focus({ preventScroll: true });
  }, [title, trocou]);

  return (
    <div className="relative isolate min-h-dvh overflow-hidden bg-bg">
      <motion.div
        aria-hidden
        className="fundo-prancha pointer-events-none absolute inset-0 -z-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, ease: EASE_OUT_QUINT }}
      />

      <div className="flex min-h-dvh flex-col items-center px-4 py-8 sm:py-10">
        <main className="flex w-full max-w-[400px] flex-1 flex-col justify-center">
          {/*
           * A marca: a palavra viva do trilho, na cor do texto. O tamanho
           * acompanha a tela — no celular, a palavra inteira cabe com folga
           * para o quadro da lente —, e trocar de tamanho remonta o desenho.
           */}
          <motion.div
            className="flex flex-col items-center text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, ease: EASE_OUT_QUINT }}
          >
            <div className={larga ? 'h-[112px]' : 'h-[88px]'}>
              <TechText
                key={larga ? 'larga' : 'estreita'}
                text="Obras Hoje"
                fontSize={larga ? 60 : 42}
                letterSpacing={0.03}
                strokeWidth={1}
                dashLength={5}
                dashGap={4}
                specks={24}
                className="text-ink"
              />
            </div>
            <motion.p
              className="-mt-2 text-[14px] text-ink-3 sm:-mt-3"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: EASE_OUT_QUINT, delay: 0.12 }}
            >
              {SLOGAN}
            </motion.p>
          </motion.div>

          <Moldura className="mt-7 p-2" atraso={0.15}>
            <motion.div
              className="border border-line bg-surface px-6 py-7 sm:px-9 sm:py-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, ease: EASE_OUT_QUINT, delay: 0.2 }}
            >
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.32, ease: EASE_OUT_QUINT, delay: atraso }}
              >
                {Icone ? (
                  <span className="mb-5 grid size-11 place-items-center border border-line bg-sunken text-ink-2">
                    <Icone size={20} strokeWidth={1.5} aria-hidden />
                  </span>
                ) : null}

                <h1
                  ref={tituloRef}
                  tabIndex={-1}
                  className="text-[22px] font-semibold tracking-tight outline-none"
                >
                  {title}
                </h1>
                {description ? (
                  <p className="mt-1.5 text-[13px] text-ink-3">{description}</p>
                ) : null}

                <div className="mt-6">{children}</div>

                {footer ? (
                  <div className="mt-6 border-t border-line pt-5 text-[13px] text-ink-3">
                    {footer}
                  </div>
                ) : null}
              </motion.div>
            </motion.div>
          </Moldura>
        </main>

        <Copyright />
      </div>
    </div>
  );
}

/**
 * Autoria do sistema: nota de rodapé, no pé da coluna, longe da marca do
 * produto.
 */
function Copyright() {
  return (
    <p className="mt-8 text-center text-[12px] text-ink-3">
      © {new Date().getFullYear()}{' '}
      <a
        href="https://adaptta.com.br"
        target="_blank"
        rel="noreferrer noopener"
        className="font-medium transition-colors hover:text-ink"
      >
        Adaptta
      </a>
    </p>
  );
}
